import { useState, useEffect } from 'react'
import {
  Wifi,
  Users,
  UserPlus,
  Search,
  Settings,
  Globe,
  Lock,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Clock,
  AlertCircle
} from 'lucide-react'
import { useP2PStore } from '../../store/p2pStore'
import { PeerConnection } from '../../types'
import { toast, TOAST_GROUPS } from '../../utils/toastManager'

interface ConnectionWizardProps {
  isOpen: boolean
  onClose: () => void
  initialStep?: 'network-init' | 'peer-discovery'
}

interface NetworkInitData {
  alias: string
  displayName: string
  group: string
  connectionMode: 'public' | 'group' | 'private'
  autoAcceptEnabled: boolean
}

interface PeerConnectionData {
  method: 'alias' | 'peerId'
  targetAlias: string
  targetPeerId: string
}

export function ConnectionWizard({ isOpen, onClose, initialStep = 'network-init' }: ConnectionWizardProps) {
  const [currentStep, setCurrentStep] = useState<'network-init' | 'peer-discovery' | 'completed'>(initialStep)
  const [networkData, setNetworkData] = useState<NetworkInitData>({
    alias: '',
    displayName: '',
    group: 'Default',
    connectionMode: 'public',
    autoAcceptEnabled: true
  })
  const [peerData, setPeerData] = useState<PeerConnectionData>({
    method: 'alias',
    targetAlias: '',
    targetPeerId: ''
  })
  const [isProcessing, setIsProcessing] = useState(false)

  // Direct store access for maximum stability (avoid multiple hook calls)
  const store = useP2PStore()
  const {
    localSelfta,
    connections,
    initializeNetwork,
    connectByAlias,
    connectToPeer,
    isInitializing,
    isNetworkStabilized,
    connectionProgress,
    getAliasRegistrations,
    isOnline,
    networkError
  } = store

  // Derived connection status
  const connectionStatus = {
    isOnline,
    isInitializing,
    isNetworkStabilized,
    connectionProgress,
    connectionCount: Array.from(connections.values()).length,
    error: networkError
  }

  // Initialize form with existing data if available
  useEffect(() => {
    if (localSelfta) {
      setNetworkData(prev => ({
        ...prev,
        alias: localSelfta.alias,
        displayName: localSelfta.displayName
      }))

      // If already connected, skip to peer discovery
      if (connectionStatus.isOnline && currentStep === 'network-init') {
        setCurrentStep('peer-discovery')
      }
    }
  }, [localSelfta, connectionStatus.isOnline, currentStep])

  // Known aliases for auto-complete
  const knownAliases = getAliasRegistrations().map(reg => reg.alias)
  const recentConnections = Array.from(connections.values())
    .sort((a, b) => (b.lastSeen?.getTime() || 0) - (a.lastSeen?.getTime() || 0))
    .slice(0, 5)

  const handleNetworkInit = async () => {
    if (!networkData.alias.trim() || !networkData.displayName.trim()) {
      toast.error('Please enter both alias and display name to continue', TOAST_GROUPS.CONNECTION, {
        duration: 4000,
        icon: '⚠️'
      })
      return
    }

    setIsProcessing(true)

    try {
      const selfta = {
        id: '', // Will be assigned by PeerJS
        alias: networkData.alias.trim(),
        displayName: networkData.displayName.trim(),
        statusMessage: `Connected to ${networkData.group} group`,
        createdAt: new Date(),
        lastUpdated: new Date()
      }

      const success = await initializeNetwork(selfta)

      if (success) {
        toast.success(`Successfully joined P2P network as '${networkData.alias}'`, TOAST_GROUPS.CONNECTION, {
          duration: 5000,
          icon: '🌐'
        })
        setCurrentStep('peer-discovery')
      } else {
        toast.error('Failed to initialize P2P network. Please try again.', TOAST_GROUPS.CONNECTION, {
          duration: 5000,
          icon: '❌'
        })
      }
    } catch (error) {
      console.error('Network initialization error:', error)
      toast.error('Network initialization failed. Please check your connection and try again.', TOAST_GROUPS.CONNECTION, {
        duration: 6000,
        icon: '❌'
      })
    } finally {
      setIsProcessing(false)
    }
  }

  const handlePeerConnection = async () => {
    if (peerData.method === 'alias' && !peerData.targetAlias.trim()) {
      toast.error('Please enter the alias of the peer you want to connect to', TOAST_GROUPS.CONNECTION, {
        duration: 4000,
        icon: '⚠️'
      })
      return
    }

    if (peerData.method === 'peerId' && !peerData.targetPeerId.trim()) {
      toast.error('Please enter a valid peer ID to connect', TOAST_GROUPS.CONNECTION, {
        duration: 4000,
        icon: '⚠️'
      })
      return
    }

    setIsProcessing(true)

    try {
      let success = false

      if (peerData.method === 'alias') {
        success = await connectByAlias(peerData.targetAlias.trim())
      } else {
        success = await connectToPeer(peerData.targetPeerId.trim())
      }

      if (success) {
        const targetName = peerData.method === 'alias' ? peerData.targetAlias : `peer ${peerData.targetPeerId.slice(-8)}`
        toast.success(`Successfully connected to '${targetName}'`, TOAST_GROUPS.CONNECTION, {
          duration: 5000,
          icon: '✅'
        })
        setCurrentStep('completed')
        setTimeout(() => {
          onClose()
        }, 2000)
      } else {
        const targetName = peerData.method === 'alias' ? peerData.targetAlias : `peer ${peerData.targetPeerId.slice(-8)}`
        toast.error(`Failed to connect to '${targetName}'. Please verify the information and try again.`, TOAST_GROUPS.CONNECTION, {
          duration: 5000,
          icon: '❌'
        })
      }
    } catch (error) {
      console.error('Peer connection error:', error)
      toast.error('Peer connection failed. Please check your network and try again.', TOAST_GROUPS.CONNECTION, {
        duration: 6000,
        icon: '❌'
      })
    } finally {
      setIsProcessing(false)
    }
  }

  const handleQuickConnect = async (connection: PeerConnection) => {
    setIsProcessing(true)
    try {
      const success = await connectToPeer(connection.peerId)
      if (success) {
        toast.success(`Successfully reconnected to '${connection.alias}'`, TOAST_GROUPS.CONNECTION, {
          duration: 5000,
          icon: '🔄'
        })
        setCurrentStep('completed')
        setTimeout(() => {
          onClose()
        }, 2000)
      } else {
        toast.error(`Failed to reconnect to '${connection.alias}'. They may be offline.`, TOAST_GROUPS.CONNECTION, {
          duration: 5000,
          icon: '❌'
        })
      }
    } catch (error) {
      console.error('Quick connect error:', error)
      toast.error('Quick reconnection failed. Please try a manual connection.', TOAST_GROUPS.CONNECTION, {
        duration: 5000,
        icon: '❌'
      })
    } finally {
      setIsProcessing(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-2 sm:p-4 mobile-safe-area">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-auto max-h-[calc(100vh-1rem)] sm:max-h-[calc(100vh-2rem)] md:max-h-[90vh] overflow-y-auto mobile-scroll-container">
        <div className="p-4 sm:p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Wifi className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {currentStep === 'network-init' && 'Join P2P Network'}
                  {currentStep === 'peer-discovery' && 'Connect to Peers'}
                  {currentStep === 'completed' && 'Connection Successful'}
                </h2>
                <p className="text-gray-600">
                  {currentStep === 'network-init' && 'Step 1: Initialize your network identity'}
                  {currentStep === 'peer-discovery' && 'Step 2: Discover and connect to peers'}
                  {currentStep === 'completed' && 'You are now connected to the P2P network'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 w-11 h-11 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors mobile-touch-target focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              aria-label="Close connection wizard"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center space-x-4">
              <div className={`flex items-center space-x-2 ${
                currentStep === 'network-init' ? 'text-blue-600' :
                currentStep === 'peer-discovery' || currentStep === 'completed' ? 'text-green-600' : 'text-gray-400'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  currentStep === 'network-init' ? 'bg-blue-100 text-blue-600' :
                  currentStep === 'peer-discovery' || currentStep === 'completed' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                }`}>
                  {currentStep === 'peer-discovery' || currentStep === 'completed' ? <CheckCircle className="w-5 h-5" /> : '1'}
                </div>
                <span className="font-medium">Network Setup</span>
              </div>

              <ArrowRight className="w-5 h-5 text-gray-400" />

              <div className={`flex items-center space-x-2 ${
                currentStep === 'peer-discovery' ? 'text-blue-600' :
                currentStep === 'completed' ? 'text-green-600' : 'text-gray-400'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  currentStep === 'peer-discovery' ? 'bg-blue-100 text-blue-600' :
                  currentStep === 'completed' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                }`}>
                  {currentStep === 'completed' ? <CheckCircle className="w-5 h-5" /> : '2'}
                </div>
                <span className="font-medium">Peer Discovery</span>
              </div>
            </div>
          </div>

          {/* Step Content */}
          {currentStep === 'network-init' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="connection-alias" className="block text-sm font-medium text-gray-700 mb-2">
                    Alias *
                  </label>
                  <input
                    id="connection-alias"
                    type="text"
                    value={networkData.alias}
                    onChange={(e) => setNetworkData(prev => ({ ...prev, alias: e.target.value }))}
                    placeholder="Your unique alias"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={connectionStatus.isOnline}
                  />
                </div>
                <div>
                  <label htmlFor="connection-display-name" className="block text-sm font-medium text-gray-700 mb-2">
                    Display Name *
                  </label>
                  <input
                    id="connection-display-name"
                    type="text"
                    value={networkData.displayName}
                    onChange={(e) => setNetworkData(prev => ({ ...prev, displayName: e.target.value }))}
                    placeholder="Your display name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={connectionStatus.isOnline}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="connection-group" className="block text-sm font-medium text-gray-700 mb-2">
                  Group
                </label>
                <select
                  id="connection-group"
                  value={networkData.group}
                  onChange={(e) => setNetworkData(prev => ({ ...prev, group: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={connectionStatus.isOnline}
                >
                  <option value="Default">Default</option>
                  <option value="Friends">Friends</option>
                  <option value="Work">Work</option>
                  <option value="Family">Family</option>
                </select>
              </div>

              <fieldset>
                <legend className="block text-sm font-medium text-gray-700 mb-3">
                  Connection Mode
                </legend>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3" role="radiogroup">
                  {[
                    { value: 'public', icon: Globe, label: 'Public', desc: 'Accept all connections' },
                    { value: 'group', icon: Users, label: 'Group Only', desc: 'Only same group members' },
                    { value: 'private', icon: Lock, label: 'Private', desc: 'Manual approval required' }
                  ].map((mode) => (
                    <button
                      key={mode.value}
                      onClick={() => setNetworkData(prev => ({ ...prev, connectionMode: mode.value as any }))}
                      className={`p-3 border rounded-lg text-left transition-colors ${
                        networkData.connectionMode === mode.value
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      disabled={connectionStatus.isOnline}
                      role="radio"
                      aria-checked={networkData.connectionMode === mode.value}
                      aria-describedby={`mode-${mode.value}-desc`}
                    >
                      <div className="flex items-center space-x-2 mb-1">
                        <mode.icon className="w-4 h-4" />
                        <span className="font-medium">{mode.label}</span>
                      </div>
                      <p className="text-xs text-gray-600" id={`mode-${mode.value}-desc`}>{mode.desc}</p>
                    </button>
                  ))}
                </div>
              </fieldset>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="autoAccept"
                  checked={networkData.autoAcceptEnabled}
                  onChange={(e) => setNetworkData(prev => ({ ...prev, autoAcceptEnabled: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  disabled={connectionStatus.isOnline}
                />
                <label htmlFor="autoAccept" className="text-sm text-gray-700">
                  Auto-accept incoming connections
                </label>
              </div>

              {/* Network Status and Progress */}
              {connectionStatus.isOnline && !isNetworkStabilized && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-4 h-4 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
                    <span className="text-amber-800 font-medium">Network Stabilizing</span>
                  </div>
                  <p className="text-amber-700 text-sm mb-3">{connectionProgress.message}</p>
                  {connectionProgress.progress !== undefined && (
                    <div className="w-full bg-amber-200 rounded-full h-2">
                      <div
                        className="bg-amber-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${connectionProgress.progress}%` }}
                      />
                    </div>
                  )}
                </div>
              )}

              {connectionStatus.isOnline && isNetworkStabilized && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-green-800 font-medium">
                      Network ready - connected as {localSelfta?.alias}
                    </span>
                  </div>
                  <p className="text-green-700 text-sm mt-1">
                    Network is stabilized and ready for peer connections.
                  </p>
                </div>
              )}

              {isInitializing && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    <span className="text-blue-800 font-medium">Initializing Network</span>
                  </div>
                  <p className="text-blue-700 text-sm mb-3">{connectionProgress.message}</p>
                  {connectionProgress.progress !== undefined && (
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${connectionProgress.progress}%` }}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Mobile-First Button Layout: Single Row with Proper Touch Targets */}
              <div className="flex flex-row items-center justify-between gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={onClose}
                  className="flex-1 min-h-[50px] px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors mobile-touch-target focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  aria-label="Cancel and close wizard"
                >
                  Cancel
                </button>
                {connectionStatus.isOnline ? (
                  <button
                    onClick={() => setCurrentStep('peer-discovery')}
                    className="flex-1 min-h-[50px] px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center space-x-2 mobile-touch-target focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    aria-label="Proceed to peer discovery step"
                  >
                    <span>Next</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={handleNetworkInit}
                    disabled={isProcessing || isInitializing}
                    className="flex-1 min-h-[50px] px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center space-x-2 mobile-touch-target focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    aria-label="Initialize network connection"
                  >
                    {isProcessing || isInitializing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Connecting...</span>
                      </>
                    ) : (
                      <>
                        <span>Join Network</span>
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}

          {currentStep === 'peer-discovery' && (
            <div className="space-y-6">
              {/* Direct Connection Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-800">Direct Alias Connections</p>
                    <p className="text-blue-700 mt-1">
                      Enter the exact alias of the person you want to connect to.
                      If they're online with that alias, connection will be instant.
                    </p>
                  </div>
                </div>
              </div>

              {/* Connection Method */}
              <fieldset>
                <legend className="block text-sm font-medium text-gray-700 mb-3">
                  Connection Method
                </legend>
                <div className="flex space-x-4" role="radiogroup">
                  <button
                    onClick={() => setPeerData(prev => ({ ...prev, method: 'alias' }))}
                    className={`px-4 py-2 rounded-lg border ${
                      peerData.method === 'alias'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 text-gray-700 hover:border-gray-400'
                    }`}
                    role="radio"
                    aria-checked={peerData.method === 'alias'}
                  >
                    <div className="flex items-center space-x-2">
                      <UserPlus className="w-4 h-4" />
                      <span>By Alias (Recommended)</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setPeerData(prev => ({ ...prev, method: 'peerId' }))}
                    className={`px-4 py-2 rounded-lg border ${
                      peerData.method === 'peerId'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 text-gray-700 hover:border-gray-400'
                    }`}
                    role="radio"
                    aria-checked={peerData.method === 'peerId'}
                  >
                    <div className="flex items-center space-x-2">
                      <Settings className="w-4 h-4" />
                      <span>By Peer ID (Legacy)</span>
                    </div>
                  </button>
                </div>
              </fieldset>

              {/* Connection Input */}
              {peerData.method === 'alias' && (
                <div>
                  <label htmlFor="target-peer-alias" className="block text-sm font-medium text-gray-700 mb-2">
                    Target Peer Alias
                  </label>
                  <div className="relative">
                    <input
                      id="target-peer-alias"
                      type="text"
                      value={peerData.targetAlias}
                      onChange={(e) => setPeerData(prev => ({ ...prev, targetAlias: e.target.value }))}
                      placeholder="e.g., LK1, LK2, MyFriend"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      list="known-aliases"
                    />
                    <datalist id="known-aliases">
                      {knownAliases.map(alias => (
                        <option key={alias} value={alias} />
                      ))}
                    </datalist>
                    <Search className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
                  </div>
                  <div className="text-xs text-gray-500 mt-1 space-y-1">
                    <p>• Enter the exact alias the other person is using</p>
                    <p>• Connection will be direct if they're online</p>
                    {knownAliases.length > 0 && (
                      <p>• {knownAliases.length} known aliases available in dropdown</p>
                    )}
                  </div>
                </div>
              )}

              {peerData.method === 'peerId' && (
                <div>
                  <label htmlFor="peer-id-direct" className="block text-sm font-medium text-gray-700 mb-2">
                    Peer ID
                  </label>
                  <input
                    id="peer-id-direct"
                    type="text"
                    value={peerData.targetPeerId}
                    onChange={(e) => setPeerData(prev => ({ ...prev, targetPeerId: e.target.value }))}
                    placeholder="Enter peer's ID"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              )}

              {/* Recent Connections */}
              {recentConnections.length > 0 && (
                <div>
                  <h3 className="block text-sm font-medium text-gray-700 mb-3">
                    Recent Connections
                  </h3>
                  <div className="space-y-2" role="list">
                    {recentConnections.map(connection => (
                      <button
                        key={connection.id}
                        onClick={() => handleQuickConnect(connection)}
                        disabled={isProcessing}
                        className="w-full flex items-center justify-between p-3 border border-gray-300 rounded-lg hover:border-gray-400 text-left disabled:opacity-50"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-600">
                              {(connection.alias || 'U').charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {/* CRITICAL FIX: Proper alias resolution for connection wizard */}
                              {connection.selfta?.displayName || connection.displayName || connection.selfta?.alias || connection.alias || `User_${connection.id?.slice(-8) || 'Unknown'}`}
                            </p>
                            <p className="text-xs text-gray-500 flex items-center space-x-1">
                              <Clock className="w-3 h-3" />
                              <span>
                                {connection.lastSeen ?
                                  new Date(connection.lastSeen).toLocaleDateString() :
                                  'Recently'
                                }
                              </span>
                            </p>
                          </div>
                        </div>
                        <div className={`w-2 h-2 rounded-full ${
                          connection.isConnected ? 'bg-green-500' : 'bg-gray-400'
                        }`} />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Connection Progress Indicator */}
              {connectionProgress.phase === 'discovering' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-4 h-4 border-2 border-yellow-600 border-t-transparent rounded-full animate-spin" />
                    <span className="text-yellow-800 font-medium">Searching Network</span>
                  </div>
                  <p className="text-yellow-700 text-sm mb-3">{connectionProgress.message}</p>
                  <div className="w-full bg-yellow-200 rounded-full h-2">
                    <div className="bg-yellow-600 h-2 rounded-full animate-pulse" style={{ width: '60%' }} />
                  </div>
                </div>
              )}

              {/* Network Stabilization Check */}
              {!isNetworkStabilized && connectionStatus.isOnline && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                    <span className="text-amber-800 font-medium">Network Still Stabilizing</span>
                  </div>
                  <p className="text-amber-700 text-sm mt-1">
                    Please wait for network stabilization before attempting alias-based connections for better reliability.
                  </p>
                </div>
              )}

              {/* Current Network Status */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Wifi className="w-5 h-5 text-blue-600" />
                  <span className="text-blue-800 font-medium">Network Status</span>
                </div>
                <div className="text-sm text-blue-700 space-y-1">
                  <p>Connected as: <strong>{localSelfta?.alias}</strong></p>
                  <p>Active connections: <strong>{connections.size}</strong></p>
                  <p>Known aliases: <strong>{knownAliases.length}</strong></p>
                  <p>Network stabilized: <strong>{isNetworkStabilized ? 'Yes' : 'No'}</strong></p>
                </div>
              </div>

              {/* Mobile-First Three-Button Layout: Back, Cancel, Connect */}
              <div className="space-y-3">
                {peerData.method === 'alias' && !isNetworkStabilized && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs text-amber-700 text-center">
                      Alias-based connections are disabled until network stabilization completes for better reliability.
                    </p>
                  </div>
                )}
                <div className="flex flex-row items-center gap-2 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setCurrentStep('network-init')}
                    className="flex-1 min-h-[50px] px-3 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium flex items-center justify-center space-x-1 mobile-touch-target focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                    aria-label="Go back to network setup"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span className="hidden xs:inline">Back</span>
                  </button>
                  <button
                    onClick={onClose}
                    className="flex-1 min-h-[50px] px-3 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium mobile-touch-target focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                    aria-label="Cancel and close wizard"
                  >
                    <span className="hidden xs:inline">Cancel</span>
                    <span className="xs:hidden">✕</span>
                  </button>
                  <button
                    onClick={handlePeerConnection}
                    disabled={
                      isProcessing ||
                      (!peerData.targetAlias.trim() && !peerData.targetPeerId.trim()) ||
                      (peerData.method === 'alias' && !isNetworkStabilized)
                    }
                    className="flex-2 min-h-[50px] px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center space-x-2 mobile-touch-target focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    aria-label="Connect to selected peer"
                    style={{ flexGrow: 2 }}
                  >
                    {isProcessing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Connecting...</span>
                      </>
                    ) : (
                      <>
                        <span>
                          {peerData.method === 'alias' && !isNetworkStabilized ? 'Wait...' : 'Connect'}
                        </span>
                        <UserPlus className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {currentStep === 'completed' && (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Connection Successful!
                </h3>
                <p className="text-gray-600">
                  You are now connected to the P2P network and can start communicating with peers.
                </p>
              </div>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Continue to Dashboard
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}